// @owners { team: patients-team }
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import { MAIL_REQUEST_CONFIRMED, NO_ORDER_ITEMS } from '~shared/features/checkout/constants';
import { useCheckoutDateErrors } from '~shared/features/checkout/hooks/useCheckoutDateErrors';
import { useCheckoutPaymentErrors } from '~shared/features/checkout/hooks/useCheckoutPaymentErrors';
import { useCheckoutSharpsContainerErrors } from '~shared/features/checkout/hooks/useCheckoutSharpsContainerErrors';
import { useCheckoutTipErrors } from '~shared/features/checkout/hooks/useCheckoutTipErrors';
import { getHasOrderItems, getOrderItemCount } from '~shared/features/checkout/selectors/getOrder';
import { getOrderIndex } from '~shared/features/checkout/selectors/getOrdersByIndex';
import { type CheckoutValidationErrors, type ValidationError } from '~shared/features/checkout/types';
import { useSelectorShared } from '~shared/store';
import { useCheckoutAddressErrors } from './useCheckoutAddressErrors';
import { useCheckoutMailRequest } from './useCheckoutMailRequest';

export const useCheckoutValidationErrors = () => {
  const hasOrderItems = useSelectorShared(getHasOrderItems);
  const orderItemCount = useSelectorShared(getOrderItemCount);
  const orderIndex = useSelectorShared(getOrderIndex);
  const { deliveryMethods } = useCartDeliveryMethods();
  const isLastShipment = orderIndex === deliveryMethods.length - 1;

  const orderValidationErrors: ValidationError[] = hasOrderItems
    ? []
    : [{ key: NO_ORDER_ITEMS, message: 'Please select one or more prescriptions.' }];
  const { addressValidationErrors } = useCheckoutAddressErrors();
  const { dateValidationErrors } = useCheckoutDateErrors(false);
  const { tipValidationErrors } = useCheckoutTipErrors();
  const paymentErrors = useCheckoutPaymentErrors();
  const { hasMailRequestConfirmedError } = useCheckoutMailRequest();
  const medicationText = orderItemCount > 1 ? 'these medications' : 'this medication';
  const mailRequestErrors: ValidationError[] = hasMailRequestConfirmedError
    ? [
        {
          key: MAIL_REQUEST_CONFIRMED,
          message: `To proceed, please check the box requesting ${medicationText} be delivered by mail.`,
        },
      ]
    : [];

  const { sharpsContainerValidationErrors } = useCheckoutSharpsContainerErrors();
  // sharps container is only shown on the last checkout screen of an order
  const contextualSharpContainerErrors: ValidationError[] = isLastShipment ? sharpsContainerValidationErrors : [];

  const validationErrors: CheckoutValidationErrors = {
    address: addressValidationErrors,
    date: dateValidationErrors,
    order: orderValidationErrors,
    payment: paymentErrors,
    sharpsContainer: contextualSharpContainerErrors,
    tip: tipValidationErrors,
    mailRequest: mailRequestErrors,
  };

  return {
    addressValidationErrors,
    dateValidationErrors,
    hasValidationErrors:
      addressValidationErrors.length > 0 ||
      dateValidationErrors.length > 0 ||
      orderValidationErrors.length > 0 ||
      paymentErrors.length > 0 ||
      contextualSharpContainerErrors.length > 0 ||
      tipValidationErrors.length > 0 ||
      mailRequestErrors.length > 0,
    paymentErrors,
    sharpsContainerValidationErrors: contextualSharpContainerErrors,
    tipValidationErrors,
    validationErrors,
  };
};
